import React, {useMemo, useState} from 'react';
import { Card, Select, Table, Input, Slider, Divider, Typography, message, Row, Col, Space, Spin } from 'antd';
import Button from '@mui/material/Button';
import { round } from 'lodash';
import useOptimizers from "../../hooks/useOptimizers";
import { useDispatch, useSelector } from 'react-redux';
import { optimizeShares } from '../../api/optimizeApi';
import { Modal, notification } from 'antd';
import {money, stringMixin} from "../../utils/strings";
const { Option } = Select;
const { Title } = Typography;

const ExportToolbar = ({ displayMode, onDisplayModeChange, onExport }) => {
  const [exportFormat, setExportFormat] = React.useState('DEFAULT');

  return (
    <Row justify={'space-between'} gutter={32} align={'middle'} className={'bordered round-2 mb-6 p-4'}>
      <Col>
        <Row justify={'start'} gutter={32} align={'middle'}>
          <Col>
            <Space>
              <Typography.Text strong>Allocation Type</Typography.Text>
              <Select
                value={displayMode}
                onChange={onDisplayModeChange}
                style={{ width: 120 }}
              >
                <Option value="%">Percentage</Option>
                <Option value="Shares">Shares</Option>
              </Select>
            </Space>
          </Col>
          <Col>
            <Space>
              <Typography.Text strong>Custodian Format</Typography.Text>
              <Select
                value={exportFormat}
                onChange={setExportFormat}
                style={{ width: 160 }}
              >
                <Option value="DEFAULT">Default Format</Option>
                <Option value="ADVYZON">Advyzon</Option>
                <Option value="FIDELITY">Fidelity</Option>
                <Option value="SCHWAB">Schwab</Option>
              </Select>
            </Space>
          </Col>
        </Row>
      </Col>
      <Col>
        <Button
          variant={'outlined'}
          size={'large'}
          onClick={() => onExport(exportFormat)}
        >
          Download CSV
        </Button>
      </Col>
    </Row>
  );
};
const PercentModeView = ({ portfolio }) => {
  const columns = [
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      render: (text) => text.toUpperCase(),
    },
    {
      title: 'Weight (%)',
      dataIndex: 'wgt',
      key: 'wgt',
      render: (value) => (value * 100).toFixed(2),
    }
  ];

  return (
    <Table
      dataSource={portfolio}
      columns={columns}
      rowKey="symbol"
      pagination={false}
    />
  );
};

const SharesToolbar = ({ settings, onSettingsChange, handleOptimize }) => {
  const loadingOptimization = useSelector(state => state.largeDataAnalytics.optimization?.loading);
  return (
    <Row justify={'space-between'} gutter={32} align={'middle'} className={'bordered round-2 mb-6 p-4'}>
      <Col>
        <Row justify={'start'} gutter={32} align={'middle'}>
          <Col>
            <Space size={'large'}>
              <Typography.Text strong>Total Amount ($)</Typography.Text>
              <Input
                type="number"
                value={settings.totalAmount}
                onChange={(e) => onSettingsChange({
                  ...settings,
                  totalAmount: parseFloat(e.target.value)
                })}
                min={1}
                step={100}
              />
            </Space>
          </Col>
          <Col>
            <Space size={'large'}>
              <Typography.Text strong>Tolerance (%)</Typography.Text>
              <Select
                value={settings.tolerance.toString()}
                onChange={(value) => onSettingsChange({
                  ...settings,
                  tolerance: parseFloat(value)
                })}
                style={{ width: '100%' }}
              >
                <Option value="0.01">0.01%</Option>
                <Option value="0.001">0.001%</Option>
                <Option value="0.0001">0.0001%</Option>
              </Select>
            </Space>
          </Col>

          <Col>
            <Space >
              <Typography.Text strong>Precision</Typography.Text>
              <Slider
                value={settings.precision}
                onChange={(value) => onSettingsChange({
                  ...settings,
                  precision: value
                })}
                min={0}
                max={4}
                step={1}
                style={{width: 100}}
              />
            </Space>
          </Col>
        </Row>
      </Col>
      <Col>
        <Button disabled={loadingOptimization} variant={'contained'} color={'info'} className={'pill'} onClick={handleOptimize}>
          {loadingOptimization ? <><Spin/> Optimizing...</> : 'Optimize Shares'}
        </Button>
      </Col>
    </Row>
  );
};



const TickerSharesSummary = ({ optimizedShares }) => {
    const totalDollars = useMemo(() => {
        return optimizedShares.reduce((sum, share) => sum + share.dollar_value, 0);
    }, [optimizedShares]);
    const maxDeviation = useMemo(() => {
        return optimizedShares.reduce((max, share) => Math.max(max, share.dev), 0);
    });
    const maxDeviationSymbol = useMemo(() => {
        return optimizedShares.find((share) => share.dev === maxDeviation)?.id;
    }, [maxDeviation]);
    const avgDeviation = useMemo(() => {
        return optimizedShares.reduce((sum, share) => sum + share.dev, 0) / optimizedShares.length;
    }, [optimizedShares]);

    return (
        <Row gutter={[16, 16]} className={'p-4'} justify="center" style={{ marginBottom: '16px' }}>
            <Col>
                <Card title={<Typography.Text strong>Capital Required</Typography.Text>}>
                    <Typography.Title level={3} style={{textAlign:'center'}}>{money(totalDollars || 0)}</Typography.Title>
                </Card>
            </Col>
            <Col>
                <Card title={<Typography.Text strong>Max Deviation</Typography.Text>}>
                    <Typography.Title level={4} style={{textAlign:'center'}}>{maxDeviationSymbol?.toUpperCase?.()} {stringMixin.pct(maxDeviation || 0)}</Typography.Title>
                </Card>
            </Col>
            <Col>
                <Card title={<Typography.Text strong>Avg Deviation</Typography.Text>}>
                    <Typography.Title level={3} style={{textAlign:'center'}}>{stringMixin?.pct?.(avgDeviation)}</Typography.Title>
                </Card>
            </Col>

        </Row>
    );
};




const SharesModeView = ({ portfolio }) => {
  const dispatch = useDispatch();
  const [settings, setSettings] = useState({
    totalAmount: 10000,
    tolerance: 0.01,
    precision: 2
  });
  const optimization = useSelector(state => state.largeDataAnalytics.optimization);
  const loadingOptimization = optimization?.loading;
  const optimizedShares = optimization?.positions;

  const handleOptimize = async () => {
    const { totalAmount, tolerance } = settings;

    const tickers = portfolio.reduce((acc, item) => {
      acc[item.symbol] = item.wgt;
      return acc;
    }, {});

    const totalWeight = Object.values(tickers).reduce((sum, weight) => sum + weight, 0);

    if (Math.abs(totalWeight - 1) > 0.01) {
      const currentTotal = round(totalWeight * 100, 2);
      const normalizedTickers = Object.entries(tickers).reduce((acc, [symbol, weight]) => {
        acc[symbol] = round(weight / totalWeight, 4);
        return acc;
      }, {});

      Modal.confirm({
        title: 'Portfolio Weights Need Normalization',
        content: (
          <div>
            <p>Your portfolio weights sum to {currentTotal}% instead of 100%.</p>
            <p>Would you like to:</p>
            <ul>
              <li>Normalize weights to sum to 100% automatically</li>
              <li>Cancel and adjust weights manually</li>
            </ul>
            <div style={{ marginTop: '10px' }}>
              <strong>Preview of normalized weights:</strong>
              <div style={{ maxHeight: '200px', overflow: 'auto', marginTop: '5px' }}>
                {Object.entries(normalizedTickers)
                  .map(([symbol, weight]) => (
                    <div key={symbol}>
                      {symbol}: {round(weight * 100, 2)}%
                      (current: {round(tickers[symbol] * 100, 2)}%)
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ),
        okText: 'Normalize Weights',
        cancelText: 'Cancel and Edit Manually',
        width: 600,
        onOk: () => {
          dispatch(optimizeShares({
            totalDollars: totalAmount,
            tolerance,
            tickers: normalizedTickers
          }));

          notification.success({
            message: 'Weights Normalized',
            description: `Portfolio weights have been adjusted from ${currentTotal}% to 100%.`
          });
        },
        onCancel: () => {
          notification.info({
            message: 'Optimization Cancelled',
            description: 'Please adjust your portfolio weights to sum to 100%.'
          });
        }
      });
    } else {
      dispatch(optimizeShares({
        totalDollars: totalAmount,
        tolerance,
        tickers
      }));
    }
  };

  const columns = [
    {
      title: 'Symbol',
      dataIndex: 'id',
      key: 'symbol',
      render: (text) => text.toUpperCase?.(),
    },
    {
      title: 'Shares',
      dataIndex: 'shares',
      key: 'shares',
      render: (value) => round(value, settings.precision).toFixed(settings.precision),
        sorter: (a, b) => a.shares - b.shares,
    },
    {
      title: 'Value ($)',
      dataIndex: 'dollar_value',
      key: 'dollar_value',
      render: (value) => value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      }),
      sorter: (a, b) => a.dollar_value - b.dollar_value,
    },
    {
      title: "Deviation",
        dataIndex: "dev",
        key: "dev",
        render: (v) => stringMixin.pct(v),
      sorter: (a, b) => a.dev - b.dev,

    },

  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <SharesToolbar
        settings={settings}
        onSettingsChange={setSettings}
        handleOptimize={handleOptimize}

      />
      {optimizedShares && <TickerSharesSummary optimizedShares={optimizedShares} />}
      <Table
        dataSource={optimizedShares || []}
        columns={columns}
        rowKey="symbol"
        pagination={false}
        bordered={true}
      />
    </div>
  );
};
const PortfolioExport = ({ portfolio, optimizedShares }) => {
  const [displayMode, setDisplayMode] = useState('%');

  const handleExport = (format) => {
    const transforms = {
      default: (row) => [
        row.symbol,
        displayMode === '%' ? (row.wgt * 100).toFixed(2) : row.shares.toFixed(2)
      ],
      advyzon: (row) => [
        row.symbol,
        displayMode === '%' ? (row.wgt * 100).toFixed(2) : null // Advyzon only supports %
      ],
      schwab: (row) => [
        row.symbol,
        displayMode === 'Shares' ? row.shares.toFixed(2) : null // Schwab only supports shares
      ],
      fidelity: (row) => [
        row.symbol,
        displayMode === '%' ? row.wgt.toString() : row.shares.toString()
      ]
    };

    // Validate if format supports current mode
    if ((format === 'advyzon' && displayMode !== '%') ||
        (format === 'schwab' && displayMode !== 'Shares')) {
      message.error(`${format} does not support ${displayMode} mode`);
      return;
    }

    const transform = transforms[format] || transforms.default;
    const headers = displayMode === '%'
      ? ['Symbol', 'Weight (%)']
      : ['Symbol', 'Shares'];

    const data = displayMode === '%' ? portfolio : optimizedShares;
    const rows = [headers, ...data.map(transform)];
    const csvContent = rows.map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portfolio_export_${format}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Card bordered={false} title={<Title>Portfolio Export</Title>} style={{maxWidth: 1200, margin: 'auto'}}>
      <div style={{ padding: 24 }}>
        <ExportToolbar
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
          onExport={handleExport}
        />
        <Divider />

        {displayMode === '%' ? (
          <PercentModeView portfolio={portfolio} />
        ) : (
          <SharesModeView
            portfolio={portfolio}
            optimizedShares={optimizedShares}
          />
        )}
      </div>
    </Card>
  );
};

export default PortfolioExport;